import { useState, useEffect, useRef, useCallback } from 'react'
import { FullScreenModal } from '../../components/ui/FullScreenModal.tsx'
import { BubbleSelect } from '../../components/ui/BubbleSelect.tsx'
import { PhotoUpload, type PhotoEntry, photoFromServer } from '../../components/ui/PhotoUpload.tsx'
import { AiSuggestionsPanel, type ApplyField, type AiSuggestions } from '../../components/ui/AiSuggestionsPanel.tsx'
import {
  gearItemsApi, fieldOptionsApi, containersApi,
  type FieldOption, type Container, type GearItem
} from '../../lib/api.ts'
import { useToast } from '../../components/ui/Toast.tsx'
import { IconLock, IconEye, IconEyeOff, IconPlus, IconCheck, IconX, IconPencil, IconSparkles } from '@tabler/icons-react'

// ── Static fallbacks ──────────────────────────────────────────────────────────

const STATIC_CONDITIONS: OptionWithId[] = ['New', 'Clean', 'Dirty', 'Filthy', 'Damaged']
  .map(l => ({ value: l.toLowerCase(), label: l, id: '' }))

const STATIC_EXCHANGE: OptionWithId[] = [
  { value: 'keep', label: 'Retain', id: '' },
  { value: 'open_to_trade_sell', label: 'Open to Offers', id: '' },
  { value: 'for_trade_sale', label: 'Listed', id: '' },
]

const STATIC_ITEM_STATUS: OptionWithId[] = [
  { value: 'active', label: 'Active', id: 'active' },
  { value: 'sold', label: 'Sold', id: 'sold' },
  { value: 'traded', label: 'Traded', id: 'traded' },
  { value: 'lost', label: 'Lost', id: 'lost' },
  { value: 'donated', label: 'Donated', id: 'donated' },
  { value: 'retired', label: 'Retired', id: 'retired' },
]

const STATIC_VISIBILITIES: OptionWithId[] = [
  { id: 'private', value: 'private', label: 'Private' },
  { id: 'shared', value: 'shared', label: 'Shared' },
  { id: 'community', value: 'community', label: 'Community' },
  { id: 'public', value: 'public', label: 'Public' },
]

// ── Visibility config — unified eye icons, semantic colors ───────────────────
// private=red, shared=orange, community=blue, public=green

type VisibilityCfg = { label: string; icon: React.ReactNode; pill: string }
const VISIBILITY_CONFIG: Record<string, VisibilityCfg> = {
  private:   { label: 'Private',   icon: <IconEyeOff size={11} />, pill: 'bg-green-950 text-green-400 border-green-800' },
  shared:    { label: 'Shared',    icon: <IconEye size={11} />,    pill: 'bg-orange-950 text-orange-400 border-orange-800' },
  community: { label: 'Community', icon: <IconEye size={11} />,    pill: 'bg-blue-950 text-blue-400 border-blue-800' },
  public:    { label: 'Public',    icon: <IconEye size={11} />,    pill: 'bg-red-950 text-red-400 border-red-800' },
}

// ── Types ─────────────────────────────────────────────────────────────────────

type OptionWithId = { value: string; label: string; id: string; hex?: string }

function toOptions(items: FieldOption[]): OptionWithId[] {
  return items.map(i => ({ value: i.value, label: i.displayLabel, id: i.id, hex: i.hexValue ?? undefined }))
}

type FormState = {
  title: string; brandId: string; description: string; notes: string
  conditionId: string; statusId: string; exchangeAvailabilityId: string; visibilityId: string
  lockerId: string; containerId: string
  sizeCategoryId: string; size: string
  activityIds: string[]; roleIds: string[]; sceneIds: string[]
  additiveIds: string[]; colorIds: string[]
  photos: PhotoEntry[]
  acquisitionSource: string; acquisitionPrice: string
  acquisitionDate: string; tradeSellValue: string; productId: string
  _brandOwnsTitle: boolean
}

const EMPTY: FormState = {
  title: '', brandId: '', description: '', notes: '',
  conditionId: '', statusId: '', exchangeAvailabilityId: '', visibilityId: '',
  lockerId: '', containerId: '',
  sizeCategoryId: '', size: '',
  activityIds: [], roleIds: [], sceneIds: [], additiveIds: [], colorIds: [],
  photos: [],
  acquisitionSource: '', acquisitionPrice: '', acquisitionDate: '', tradeSellValue: '', productId: '',
  _brandOwnsTitle: false,
}

// Build form state from an existing GearItem for edit/view mode
function formFromItem(item: GearItem): FormState {
  const getAttrIds = (type: string) =>
    item.attributes
      .filter(a => a.attributeType === type)
      .sort((a, b) => a.priority - b.priority)
      .map(a => a.valueId)

  return {
    title: item.title,
    brandId: '',            // brand is stored as text — resolved below
    description: item.description ?? '',
    notes: item.notes ?? '',
    conditionId: item.conditionId ?? '',
    statusId: item.statusId ?? '',
    exchangeAvailabilityId: item.exchangeAvailabilityId ?? '',
    visibilityId: item.visibilityId ?? '',
    lockerId: '',           // resolved from containerId below
    containerId: item.containerId ?? '',
    sizeCategoryId: item.sizeCategoryId ?? '',
    size: item.size ?? '',
    activityIds: getAttrIds('activity'),
    roleIds: getAttrIds('role'),
    sceneIds: getAttrIds('scene'),
    additiveIds: getAttrIds('additive'),
    colorIds: getAttrIds('color'),
    photos: item.photos
      .sort((a, b) => a.ordinal - b.ordinal)
      .map(p => photoFromServer(p)),
    acquisitionSource: item.acquisitionSource ?? '',
    acquisitionPrice: item.acquisitionPrice ?? '',
    acquisitionDate: item.acquisitionDate ?? '',
    tradeSellValue: item.tradeSellValue ?? '',
    productId: (item as any).productId ?? '',
    _brandOwnsTitle: false,
  }
}

// ── UI Primitives ─────────────────────────────────────────────────────────────

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-surface-overlay border border-border-default rounded-xl p-4 ${className}`}>
      {children}
    </div>
  )
}

function SubCard({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-surface-raised border border-border-subtle rounded-lg p-3 ${className}`}>
      {children}
    </div>
  )
}

// Card title row with optional visibility pill
function CardHeader({ title, pill, required }: { title: string; pill?: React.ReactNode; required?: boolean }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs font-medium uppercase tracking-widest text-zinc-400">
        {title}{required && <span className="text-brand-orange ml-0.5">*</span>}
      </span>
      {pill}
    </div>
  )
}

function CardLabel({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <label className="block text-xs font-medium uppercase tracking-widest text-zinc-400 mb-2">
      {children}{required && <span className="text-brand-orange ml-0.5">*</span>}
    </label>
  )
}

function FieldHint({ children }: { children: React.ReactNode }) {
  return <p className="text-xs text-zinc-500 italic mb-2 leading-relaxed">{children}</p>
}

function ViewLabel({ children }: { children: React.ReactNode }) {
  return <span className="text-sm text-zinc-200">{children || <span className="text-zinc-600 italic">Not set</span>}</span>
}

function TextInput({ value, onChange, placeholder }: {
  value: string; onChange: (v: string) => void; placeholder?: string
}) {
  return (
    <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      className="w-full px-3 py-2 bg-surface-raised border border-border-default rounded-lg text-sm text-zinc-200 placeholder-zinc-500 outline-none focus:border-brand-orange/60 transition-colors" />
  )
}

function TextArea({ value, onChange, placeholder, rows = 4, className = '' }: {
  value: string; onChange: (v: string) => void; placeholder?: string; rows?: number; className?: string
}) {
  return (
    <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={rows}
      className={`w-full px-3 py-2 bg-surface-raised border border-border-default rounded-lg text-sm text-zinc-200 placeholder-zinc-500 outline-none focus:border-brand-orange/60 transition-colors resize-none ${className}`} />
  )
}

function SelectInput({ value, onChange, children, disabled }: {
  value: string; onChange: (v: string) => void; children: React.ReactNode; disabled?: boolean
}) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
      className="w-full px-3 py-2 bg-surface-raised border border-border-default rounded-lg text-sm text-zinc-200 outline-none focus:border-brand-orange/60 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
      {children}
    </select>
  )
}

// Consistent pill-sized buttons for radio-style single select
function RadioBubbles({ options, value, onChange, readOnly }: {
  options: OptionWithId[]; value: string; onChange: (id: string) => void; readOnly?: boolean
}) {
  if (readOnly) {
    const selected = options.find(o => o.id === value || o.value === value)
    return <ViewLabel>{selected?.label ?? ''}</ViewLabel>
  }
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(opt => (
        <button key={opt.id || opt.value} type="button"
          onClick={() => onChange(value === (opt.id || opt.value) ? '' : (opt.id || opt.value))}
          className={[
            'px-3 py-1.5 rounded-full text-sm border transition-all',
            (value === opt.id || value === opt.value)
              ? 'bg-brand-orange border-brand-orange text-white font-medium'
              : 'border-zinc-500 text-zinc-200 hover:border-zinc-300 hover:text-white',
          ].join(' ')}>
          {opt.label}
        </button>
      ))}
    </div>
  )
}

// ── Visibility pill ───────────────────────────────────────────────────────────

function VisibilityPill({ value }: { value: string }) {
  const cfg = VISIBILITY_CONFIG[value] ?? VISIBILITY_CONFIG.private
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border font-medium ${cfg.pill}`}>
      {cfg.icon}{cfg.label}
    </span>
  )
}

// ── Unsaved changes dialog ────────────────────────────────────────────────────

function UnsavedChangesDialog({ open, onLeave, onStay }: {
  open: boolean; onLeave: () => void; onStay: () => void
}) {
  if (!open) return null
  return (
    <div className="fixed inset-0 z-[400] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-sm bg-surface-overlay border border-border-default rounded-2xl shadow-2xl p-6">
        <h3 className="text-sm font-semibold text-zinc-100 mb-2">Unsaved changes</h3>
        <p className="text-sm text-zinc-400 mb-5">
          You have unsaved changes. If you leave now they will be lost.
        </p>
        <div className="flex gap-2">
          <button onClick={onStay}
            className="flex-1 px-4 py-2 rounded-lg text-sm border border-border-default text-zinc-400 hover:text-zinc-200 transition-colors">
            Keep editing
          </button>
          <button onClick={onLeave}
            className="flex-1 px-4 py-2 rounded-lg text-sm font-medium bg-red-900 text-red-200 hover:bg-red-800 transition-colors">
            Discard changes
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Quick-add popover (generic) ───────────────────────────────────────────────

function QuickAddPopover({ label, onSave, onClose, extraFields }: {
  label: string; onSave: (name: string) => Promise<void>
  onClose: () => void; extraFields?: React.ReactNode
}) {
  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => { ref.current?.focus() }, [])

  const handle = async () => {
    if (!name.trim()) { setErr('Required'); return }
    setSaving(true); setErr('')
    try { await onSave(name.trim()); onClose() }
    catch (e: any) { setErr(e.message ?? 'Failed') }
    finally { setSaving(false) }
  }

  return (
    <div className="absolute z-50 top-full left-0 mt-1.5 w-52 bg-zinc-900 border border-border-strong rounded-xl shadow-2xl p-3 space-y-2">
      <div className="absolute -top-1.5 left-5 w-3 h-3 bg-zinc-900 border-l border-t border-border-strong rotate-45" />
      <p className="text-[10px] font-medium uppercase tracking-widest text-zinc-400">Add {label}</p>
      <input ref={ref} value={name} onChange={e => setName(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') handle(); if (e.key === 'Escape') onClose() }}
        placeholder="Name..."
        className="w-full px-2.5 py-1.5 bg-surface-raised border border-border-default rounded-lg text-xs text-zinc-200 placeholder-zinc-600 outline-none focus:border-brand-orange/60" />
      {extraFields}
      {err && <p className="text-[10px] text-red-400">{err}</p>}
      <div className="flex gap-1.5">
        <button onClick={onClose} className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs border border-border-default text-zinc-500 hover:text-zinc-300 transition-colors">
          <IconX size={11} /> Cancel
        </button>
        <button onClick={handle} disabled={saving} className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs bg-brand-orange text-white disabled:opacity-50 transition-colors">
          <IconCheck size={11} /> {saving ? '...' : 'Add'}
        </button>
      </div>
    </div>
  )
}

// ── SelectWithAdd ─────────────────────────────────────────────────────────────

function SelectWithAdd({ value, onChange, children, disabled, addLabel, onAdd }: {
  value: string; onChange: (v: string) => void; children: React.ReactNode
  disabled?: boolean; addLabel: string; onAdd: (name: string) => Promise<void>
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [open])

  return (
    <div className="flex gap-1.5 items-start">
      <SelectInput value={value} onChange={onChange} disabled={disabled}>{children}</SelectInput>
      <div className="relative shrink-0" ref={ref}>
        <button type="button" onClick={() => setOpen(v => !v)}
          className="h-[38px] w-[38px] flex items-center justify-center rounded-lg border border-dashed border-zinc-600 text-zinc-500 hover:border-zinc-400 hover:text-zinc-300 transition-colors">
          <IconPlus size={14} />
        </button>
        {open && <QuickAddPopover label={addLabel} onSave={onAdd} onClose={() => setOpen(false)} />}
      </div>
    </div>
  )
}

// ── BinQuickAdd ───────────────────────────────────────────────────────────────

function BinQuickAdd({ lockers, bins, defaultParentId, onAdd }: {
  lockers: Container[]; bins: Container[]
  defaultParentId: string; onAdd: (name: string, parentId: string) => Promise<void>
}) {
  const [open, setOpen] = useState(false)
  const [parentId, setParentId] = useState(defaultParentId)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => { setParentId(defaultParentId) }, [defaultParentId])
  useEffect(() => {
    if (!open) return
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [open])

  const allContainers = [...lockers, ...bins]
  const getDepth = (id: string, depth = 0): number => {
    const c = allContainers.find(x => x.id === id)
    if (!c || !c.parentId) return depth
    return getDepth(c.parentId, depth + 1)
  }

  return (
    <div className="relative shrink-0" ref={ref}>
      <button type="button" onClick={() => setOpen(v => !v)}
        className="h-[38px] w-[38px] flex items-center justify-center rounded-lg border border-dashed border-zinc-600 text-zinc-500 hover:border-zinc-400 hover:text-zinc-300 transition-colors">
        <IconPlus size={14} />
      </button>
      {open && (
        <QuickAddPopover label="gear bag" onSave={n => onAdd(n, parentId)} onClose={() => setOpen(false)}
          extraFields={
            <div>
              <p className="text-[10px] text-zinc-500 mb-1">Place inside</p>
              <select value={parentId} onChange={e => setParentId(e.target.value)}
                className="w-full px-2 py-1 bg-surface-raised border border-border-default rounded text-xs text-zinc-300 outline-none">
                {lockers.map(l => <option key={l.id} value={l.id}>{l.name} | Locker</option>)}
                {bins.map(b => {
                  const depth = getDepth(b.id)
                  return <option key={b.id} value={b.id}>{'  '.repeat(depth)}{b.name} | Gear Bag</option>
                })}
              </select>
            </div>
          } />
      )}
    </div>
  )
}

// ── StoragePicker ─────────────────────────────────────────────────────────────

function StoragePicker({ lockers, bins, lockerId, containerId, onLockerChange,
  onContainerChange, loadingBins, onAddLocker, onAddBin, readOnly }: {
  lockers: Container[]; bins: Container[]
  lockerId: string; containerId: string
  onLockerChange: (id: string) => void; onContainerChange: (id: string) => void
  loadingBins: boolean; onAddLocker: (name: string) => Promise<void>
  onAddBin: (name: string, parentId: string) => Promise<void>
  readOnly?: boolean
}) {
  const allContainers = [...lockers, ...bins]
  const getDepth = (id: string, depth = 0): number => {
    const c = allContainers.find(x => x.id === id)
    if (!c || !c.parentId) return depth
    return getDepth(c.parentId, depth + 1)
  }

  if (readOnly) {
    const locker = lockers.find(l => l.id === lockerId)
    const bin = bins.find(b => b.id === containerId)
    return (
      <div className="text-sm text-zinc-200 space-y-0.5">
        <div>{locker?.name ?? <span className="text-zinc-600 italic">No locker</span>}</div>
        {bin && bin.id !== lockerId && <div className="text-xs text-zinc-400">└ {bin.name}</div>}
        {containerId === lockerId && <div className="text-xs text-zinc-400">Default Shelf</div>}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <SelectWithAdd value={lockerId} onChange={onLockerChange} addLabel="locker" onAdd={onAddLocker}>
        <option value="">Select locker...</option>
        {lockers.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
      </SelectWithAdd>
      {lockerId && (
        <div className="flex gap-1.5 items-start">
          <SelectInput value={containerId} onChange={onContainerChange} disabled={loadingBins}>
            <option value="">Select gear bag...</option>
            <option value={lockerId}>Default Shelf (directly in locker)</option>
            {bins.map(b => {
              const depth = getDepth(b.id)
              const prefix = depth > 0 ? '  '.repeat(depth) + '└ ' : ''
              return <option key={b.id} value={b.id}>{prefix}{b.name}</option>
            })}
          </SelectInput>
          <BinQuickAdd lockers={lockers} bins={bins} defaultParentId={lockerId} onAdd={onAddBin} />
        </div>
      )}
    </div>
  )
}

// ── Main GearItemForm ─────────────────────────────────────────────────────────

export type GearItemFormMode = 'new' | 'edit' | 'view'

type Props = {
  mode: GearItemFormMode
  item?: GearItem
  open: boolean
  onClose: () => void
  onSaved?: () => void
  onModeChange?: (mode: GearItemFormMode) => void
}

export function GearItemForm({ mode, item, open, onClose, onSaved, onModeChange }: Props) {
  const { success, error } = useToast()
  const isReadOnly = mode === 'view'
  const isNew = mode === 'new'

  const [form, setForm] = useState<FormState>(EMPTY)
  const [initialForm, setInitialForm] = useState<FormState>(EMPTY)
  const [saving, setSaving] = useState(false)
  const [acquisitionOpen, setAcquisitionOpen] = useState(false)
  const [showUnsaved, setShowUnsaved] = useState(false)
  const [identifying, setIdentifying] = useState(false)
  const [aiSuggestions, setAiSuggestions] = useState<AiSuggestions | null>(null)

  // Options
  const [opts, setOpts] = useState<{
    conditions: OptionWithId[]; exchange: OptionWithId[]; statuses: OptionWithId[]
    activities: OptionWithId[]; roles: OptionWithId[]; scenes: OptionWithId[]
    additives: OptionWithId[]; colors: OptionWithId[]; brands: OptionWithId[]
    visibilities: OptionWithId[]; sizeCategories: OptionWithId[]; sizes: OptionWithId[]
  }>({
    conditions: STATIC_CONDITIONS, exchange: STATIC_EXCHANGE, statuses: [],
    activities: [], roles: [], scenes: [], additives: [], colors: [], brands: [],
    visibilities: STATIC_VISIBILITIES, sizeCategories: [], sizes: [],
  })

  // Containers
  const [lockers, setLockers] = useState<Container[]>([])
  const [bins, setBins] = useState<Container[]>([])
  const [loadingBins, setLoadingBins] = useState(false)

  // Dirty tracking — compare current form to initial snapshot
  const isDirty = !isReadOnly && JSON.stringify(form) !== JSON.stringify(initialForm)

  const loadBins = useCallback(async (lockerId: string) => {
    setLoadingBins(true); setBins([])
    try {
      const res = await containersApi.list({ parentId: lockerId, type: 'bin' })
      const data = res.data ?? []
      setBins(data)
      return data
    } catch { return [] } finally { setLoadingBins(false) }
  }, [])

  // Initialise form when modal opens or mode/item changes
  useEffect(() => {
    if (!open) return

    // Load field options
    const loadOpt = async (attr: string) => {
      try { return toOptions((await fieldOptionsApi.getByAttribute(attr)).data ?? []) }
      catch { return [] }
    }

    Promise.all([
      loadOpt('condition'), loadOpt('trade-status'), loadOpt('status'),
      loadOpt('activity'), loadOpt('role'), loadOpt('scene'),
      loadOpt('additive'), loadOpt('color'), loadOpt('brand'),
      loadOpt('visibility'), loadOpt('size-category'),
    ]).then(([conditions, exchange, statuses, activities, roles, scenes,
              additives, colors, brands, visibilities, sizeCategories]) => {
      const resolvedExchange = exchange.length ? exchange.map(o => ({
        ...o,
        label: o.value === 'keep' ? 'Retain'
          : o.value === 'open_to_trade_sell' ? 'Open to Offers'
          : o.value === 'for_trade_sale' ? 'Listed' : o.label,
      })) : STATIC_EXCHANGE

      setOpts(prev => ({
        ...prev,
        conditions: conditions.length ? conditions : STATIC_CONDITIONS,
        exchange: resolvedExchange,
        statuses,
        activities, roles, scenes, additives, colors, brands,
        visibilities: visibilities.length ? visibilities : STATIC_VISIBILITIES,
        sizeCategories,
      }))
    })

    // Load lockers
    containersApi.list({ type: 'locker' }).then(async res => {
      const data = res.data ?? []
      setLockers(data)

      if (isNew) {
        // New mode: auto-select only if single locker
        if (data.length === 1) {
          const binsData = await loadBins(data[0].id)
          const initialContainerId = binsData.length === 0 ? data[0].id : ''
          const base = { ...EMPTY, lockerId: data[0].id, containerId: initialContainerId }
          setForm(base)
          setInitialForm(base)
        } else {
          setForm(EMPTY)
          setInitialForm(EMPTY)
        }
      } else if (item) {
        // Edit/view mode: populate from item
        const base = formFromItem(item)

        // Resolve lockerId from containerId
        if (item.containerId) {
          // Check if containerId is a locker
          const locker = data.find(l => l.id === item.containerId)
          if (locker) {
            base.lockerId = locker.id
            // load bins for this locker
            await loadBins(locker.id)
          } else {
            // containerId is a bin — find its parent locker
            const allBins = await containersApi.list({ type: 'bin' }).then(r => r.data ?? []).catch(() => [])
            setBins(allBins)
            const bin = allBins.find(b => b.id === item.containerId)
            if (bin?.parentId) {
              // Walk up to find the locker
              const parentLocker = data.find(l => l.id === bin.parentId)
              if (parentLocker) {
                base.lockerId = parentLocker.id
              } else {
                // Parent is another bin — just set lockerId to first locker for now
                base.lockerId = data[0]?.id ?? ''
              }
              await loadBins(base.lockerId)
            }
          }
        }

        setForm(base)
        setInitialForm(base)
        // Resolve brand after opts are loaded — use functional update to avoid race
        setOpts(prev => {
          if (item.brand) {
            const matched = prev.brands.find(b => b.label === item.brand)
            if (matched) {
              setForm(f => ({ ...f, brandId: matched.id }))
              setInitialForm(f => ({ ...f, brandId: matched.id }))
            }
          }
          return prev
        })
      }
    }).catch(() => {})
  }, [open, mode, item?.id])

  // Load sizes when size category changes
  useEffect(() => {
    if (!form.sizeCategoryId) { setOpts(prev => ({ ...prev, sizes: [] })); return }
    fieldOptionsApi.getByAttribute(`size?group_id=${form.sizeCategoryId}`)
      .then(res => setOpts(prev => ({ ...prev, sizes: toOptions(res.data ?? []) })))
      .catch(() => {})
  }, [form.sizeCategoryId])

  // Default visibility to 'private' for new items once options are loaded
  useEffect(() => {
    if (!isNew || !open) return
    const privateOpt = opts.visibilities.find(v => v.value === 'private')
    if (!privateOpt) return
    setForm(prev => prev.visibilityId ? prev : { ...prev, visibilityId: privateOpt.id })
    setInitialForm(prev => prev.visibilityId ? prev : { ...prev, visibilityId: privateOpt.id })
  }, [opts.visibilities, isNew, open])

  const handleLockerChange = async (lockerId: string) => {
    setForm(prev => ({ ...prev, lockerId, containerId: '' }))
    setBins([])
    if (lockerId) {
      const binsData = await loadBins(lockerId)
      if (binsData.length === 0) setForm(prev => ({ ...prev, containerId: lockerId }))
    }
  }

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm(prev => ({ ...prev, [key]: value }))

  // Quick-add handlers
  const handleAddLocker = async (name: string) => {
    const res = await containersApi.create({ name, typeName: 'locker' })
    const newLocker = res.data
    setLockers(prev => [...prev, newLocker])
    setForm(prev => ({ ...prev, lockerId: newLocker.id, containerId: newLocker.id }))
    await loadBins(newLocker.id)
  }

  const handleAddBin = async (name: string, parentId: string) => {
    const res = await containersApi.create({ name, typeName: 'bin', parentId })
    const newBin = res.data
    setBins(prev => [...prev, newBin])
    setForm(prev => ({ ...prev, containerId: newBin.id }))
  }

  const handleAddBrand = async (name: string) => {
    const res = await fieldOptionsApi.quickAdd('brand', { displayLabel: name })
    const opt = { id: res.data.id, value: res.data.value, label: res.data.displayLabel }
    setOpts(prev => ({ ...prev, brands: [...prev.brands, opt] }))
    handleBrandChange(opt.id, [...opts.brands, opt])
  }

  // Brand change with title auto-set logic:
  // - title empty → set to brand label + space, mark as brand-owned
  // - title exactly matches prev brand label (trimmed) → update to new brand label + space
  // - brand cleared → clear title if still brand-owned
  // NOTE: title and _brandOwnsTitle are read from `prev` inside the updater so that
  // batched state updates (e.g. Apply All in AiSuggestionsPanel) see the correct
  // sequenced state rather than a stale closure snapshot.
  const handleBrandChange = (newBrandId: string, brandList = opts.brands) => {
    const newBrand = brandList.find(b => b.id === newBrandId)
    const newLabel = newBrand ? newBrand.label + ' ' : ''

    setForm(prev => {
      const prevBrand = brandList.find(b => b.id === prev.brandId)
      const trimmedTitle = prev.title.trim()
      const titleIsBrandOwned = prev._brandOwnsTitle

      if (!newBrandId) {
        // Brand cleared
        if (titleIsBrandOwned || trimmedTitle === (prevBrand?.label ?? '').trim()) {
          return { ...prev, brandId: '', title: '', _brandOwnsTitle: false }
        }
        return { ...prev, brandId: '' }
      }

      if (trimmedTitle === '' || titleIsBrandOwned || trimmedTitle === (prevBrand?.label ?? '').trim()) {
        return { ...prev, brandId: newBrandId, title: newLabel, _brandOwnsTitle: true }
      }

      return { ...prev, brandId: newBrandId }
    })
  }

  const handleAddSizeCat = async (name: string) => {
    const res = await fieldOptionsApi.quickAdd('size-category', { displayLabel: name })
    const opt = { id: res.data.id, value: res.data.value, label: res.data.displayLabel }
    setOpts(prev => ({ ...prev, sizeCategories: [...prev.sizeCategories, opt] }))
    setForm(prev => ({ ...prev, sizeCategoryId: opt.id, size: '' }))
  }

  // Attempt close — guard unsaved changes
  const handleClose = () => {
    if (isDirty) { setShowUnsaved(true); return }
    onClose()
  }

  const handleDiscardAndClose = () => {
    setShowUnsaved(false)
    setForm(EMPTY)
    onClose()
  }

  const handleIdentify = async () => {
    const files = form.photos
      .filter(p => !p.isDeleted && p.file)
      .map(p => p.file!)
    if (files.length === 0) return
    setIdentifying(true)
    try {
      const res = await gearItemsApi.identify(files)
      setAiSuggestions(res.data)
    } catch (e: any) {
      error("AI identification failed", e.message)
    } finally {
      setIdentifying(false)
    }
  }

  const handleAiApplyField = (field: ApplyField, value: unknown) => {
    switch (field) {
      case "title":
        // Clear _brandOwnsTitle so a subsequent brand apply doesn't overwrite the AI-set title
        setForm(prev => ({ ...prev, title: value as string, _brandOwnsTitle: false }))
        break
      case "brand": handleBrandChange(value as string); break
      case "description": set("description", value as string); break
      case "condition": set("conditionId", value as string); break
      case "activities": set("activityIds", value as string[]); break
      case "colors": set("colorIds", value as string[]); break
      case "roles": set("roleIds", value as string[]); break
      case "scenes": set("sceneIds", value as string[]); break
    }
  }

  const canSave = isDirty && !!form.title.trim() && !!form.containerId

  const handleSave = async ({ keepOpen = false } = {}) => {
    if (!canSave) return
    setSaving(true)
    try {
      const attributes = [
        ...form.activityIds.map(id => ({ attributeType: 'activity', valueId: id, priority: 0 })),
        ...form.roleIds.map(id => ({ attributeType: 'role', valueId: id, priority: 0 })),
        ...form.sceneIds.map(id => ({ attributeType: 'scene', valueId: id, priority: 0 })),
        ...form.additiveIds.map(id => ({ attributeType: 'additive', valueId: id, priority: 0 })),
        ...form.colorIds.map((id, i) => ({ attributeType: 'color', valueId: id, priority: i })),
      ]

      const brandLabel = opts.brands.find(b => b.id === form.brandId)?.label ?? null

      const photoUpdates = form.photos
        .filter(p => p.isExisting && !p.isDeleted)
        .map(p => ({ id: p.id, isPrimary: p.isPrimary, ordinal: p.ordinal, altText: p.altText }))
      const deletedPhotoIds = form.photos
        .filter(p => p.isExisting && p.isDeleted)
        .map(p => p.id)

      // New photos that have an actual File object — upload them
      const newPhotoEntries = form.photos.filter(p => !p.isExisting && !p.isDeleted && p.file)

      const payload = {
        title: form.title.trim(),
        brand: brandLabel,
        description: form.description.trim() || null,
        notes: form.notes.trim() || null,
        conditionId: form.conditionId || null,
        statusId: form.statusId || null,
        visibilityId: form.visibilityId || null,
        exchangeAvailabilityId: form.exchangeAvailabilityId || null,
        containerId: form.containerId,
        sizeCategoryId: form.sizeCategoryId || null,
        size: form.size || null,
        attributes,
        photoUpdates,
        newPhotos: [], // new photos are uploaded separately after item is saved
        acquisitionSource: form.acquisitionSource.trim() || null,
        acquisitionPrice: form.acquisitionPrice || null,
        acquisitionDate: form.acquisitionDate || null,
        tradeSellValue: form.tradeSellValue || null,
        productId: form.productId.trim() || null,
      }

      let savedItemId: string

      if (isNew) {
        const res = await gearItemsApi.create(payload)
        savedItemId = res.data.id
      } else if (item) {
        // Delete removed photos
        for (const photoId of deletedPhotoIds) {
          await gearItemsApi.deletePhoto(item.id, photoId)
        }
        await gearItemsApi.update(item.id, payload)
        savedItemId = item.id
      } else {
        return
      }

      // Upload new photos to the API — storage handled server-side
      for (const photoEntry of newPhotoEntries) {
        const fd = new FormData()
        fd.append('file', photoEntry.file!)
        fd.append('isPrimary', String(photoEntry.isPrimary))
        if (photoEntry.altText) fd.append('altText', photoEntry.altText)
        await gearItemsApi.uploadPhoto(savedItemId, fd)
      }

      success('Gear saved', form.title)
      setInitialForm(form)
      onSaved?.()

      if (keepOpen) {
        // Save & Continue — stay in edit mode
      } else {
        onClose()
      }
    } catch (e: any) {
      error('Failed to save gear', e.message)
    } finally { setSaving(false) }
  }

  // Resolve visibility value string from ID
  const visibilityValue = opts.visibilities.find(v => v.id === form.visibilityId)?.value
    ?? form.visibilityId ?? ''

  // Modal title/subtitle
  const modalTitle = isNew ? (form.title.trim() || 'New gear item')
    : (item?.title ?? 'Gear item')
  const modalSubtitle = isNew ? 'Add gear to your locker'
    : mode === 'edit' ? 'Editing'
    : undefined

  // Header actions — Edit button in view mode
  const headerActions = isReadOnly ? (
    <button onClick={() => onModeChange?.('edit')}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border border-border-default text-zinc-300 hover:text-zinc-100 hover:bg-surface-raised transition-colors">
      <IconPencil size={14} /> Edit
    </button>
  ) : undefined

  // Footer — only shown in new/edit mode
  const footer = !isReadOnly ? (
    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
      <button type="button" onClick={isNew ? handleClose : () => onModeChange?.('view')}
        className="order-last sm:order-first px-4 py-2 rounded-lg text-sm text-zinc-400 hover:text-zinc-200 hover:bg-surface-raised transition-colors text-center">
        Cancel
      </button>
      <div className="flex gap-2">
        <button type="button" onClick={() => handleSave({ keepOpen: true })}
          disabled={saving || !canSave}
          className="flex-1 sm:flex-none px-4 py-2 rounded-lg text-sm border border-border-default text-zinc-300 hover:text-zinc-100 hover:bg-surface-raised disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
          {saving ? 'Saving...' : 'Save & Continue'}
        </button>
        <button type="button" onClick={() => handleSave({ keepOpen: false })}
          disabled={saving || !canSave}
          className="flex-1 sm:flex-none px-5 py-2 rounded-lg text-sm font-medium bg-brand-orange text-white hover:bg-brand-orange-dark disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
          {saving ? 'Saving...' : 'Save & Close'}
        </button>
      </div>
    </div>
  ) : undefined

  return (
    <>
      <FullScreenModal
        open={open}
        onClose={handleClose}
        title={modalTitle}
        subtitle={modalSubtitle}
        footer={footer}
        headerActions={headerActions}
        wide>

        <div className="px-3 py-4 sm:px-6 sm:py-5 space-y-4">

          {/* ── Row 1: Item Summary (60%) + Visibility & Storage (40%) ── */}
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
            <Card className="col-span-3 flex flex-col">
              <CardHeader title="Item Summary" pill={visibilityValue ? <VisibilityPill value={visibilityValue} /> : undefined} />
              <div className="space-y-3 flex-1">
                <div>
                  <CardLabel required={!isReadOnly}>Name</CardLabel>
                  {isReadOnly
                    ? <ViewLabel>{form.title}</ViewLabel>
                    : <TextInput value={form.title} onChange={v => setForm(prev => ({ ...prev, title: v, _brandOwnsTitle: false }))}
                        placeholder="e.g. Bauer Supreme hockey jersey" />}
                </div>
                <div>
                  <CardLabel>Item Status</CardLabel>
                  <RadioBubbles options={opts.statuses.length ? opts.statuses : STATIC_ITEM_STATUS} value={form.statusId}
                    onChange={v => set('statusId', v)} readOnly={isReadOnly} />
                </div>
                <div className="flex-1">
                  <CardLabel>Description</CardLabel>
                  {isReadOnly
                    ? <ViewLabel>{form.description}</ViewLabel>
                    : <TextArea value={form.description} onChange={v => set('description', v)}
                        placeholder="Describe this item..." rows={4} />}
                </div>
              </div>
            </Card>

            <div className="col-span-2 flex flex-col gap-4">
              <Card className="flex flex-col flex-1">
                <CardHeader title="Visibility" pill={visibilityValue ? <VisibilityPill value={visibilityValue} /> : undefined} />
                {isReadOnly
                  ? <ViewLabel>{opts.visibilities.find(v => v.id === form.visibilityId)?.label ?? ''}</ViewLabel>
                  : <SelectInput value={form.visibilityId} onChange={v => set('visibilityId', v)}>
                      <option value="">Select...</option>
                      {opts.visibilities.map(v => <option key={v.id} value={v.id}>{v.label}</option>)}
                    </SelectInput>}
              </Card>
              <Card className="flex flex-col flex-1">
                <CardHeader title="Storage Location" pill={<VisibilityPill value="private" />} />
                <StoragePicker
                  lockers={lockers} bins={bins}
                  lockerId={form.lockerId} containerId={form.containerId}
                  onLockerChange={handleLockerChange}
                  onContainerChange={v => set('containerId', v)}
                  loadingBins={loadingBins}
                  onAddLocker={handleAddLocker}
                  onAddBin={handleAddBin}
                  readOnly={isReadOnly} />
              </Card>
            </div>
          </div>

          {/* ── Row 2: Item Details (40%) + Attachments (60%) ── */}
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
            <Card className="col-span-2 flex flex-col">
              <CardHeader title="Item Details" pill={visibilityValue ? <VisibilityPill value={visibilityValue} /> : undefined} />
              <div className="space-y-4 flex-1">
                <div>
                  <CardLabel>Brand</CardLabel>
                  {isReadOnly
                    ? <ViewLabel>{opts.brands.find(b => b.id === form.brandId)?.label ?? item?.brand ?? ''}</ViewLabel>
                    : <SelectWithAdd value={form.brandId} onChange={v => handleBrandChange(v)}
                        addLabel="brand" onAdd={handleAddBrand}>
                        <option value="">Select brand...</option>
                        {opts.brands.map(b => <option key={b.id} value={b.id}>{b.label}</option>)}
                      </SelectWithAdd>}
                </div>
                <div>
                  <CardLabel>Condition</CardLabel>
                  <RadioBubbles options={opts.conditions} value={form.conditionId}
                    onChange={v => set('conditionId', v)} readOnly={isReadOnly} />
                </div>
                <div>
                  <CardLabel>Size Category</CardLabel>
                  {isReadOnly
                    ? <ViewLabel>{opts.sizeCategories.find(s => s.id === form.sizeCategoryId)?.label ?? ''}</ViewLabel>
                    : <SelectWithAdd value={form.sizeCategoryId}
                        onChange={v => { set('sizeCategoryId', v); set('size', '') }}
                        addLabel="size category" onAdd={handleAddSizeCat}>
                        <option value="">Select...</option>
                        {opts.sizeCategories.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                      </SelectWithAdd>}
                </div>
                <div>
                  <CardLabel>Size</CardLabel>
                  {isReadOnly
                    ? <ViewLabel>{opts.sizes.find(s => s.value === form.size)?.label ?? form.size}</ViewLabel>
                    : <SelectWithAdd value={form.size}
                        onChange={v => set('size', v)}
                        addLabel="size"
                        onAdd={async (name) => {
                          const res = await fieldOptionsApi.quickAdd(`size?group_id=${form.sizeCategoryId}`, { displayLabel: name, groupId: form.sizeCategoryId })
                          const opt = { id: res.data.id, value: res.data.value, label: res.data.displayLabel }
                          setOpts(prev => ({ ...prev, sizes: [...prev.sizes, opt] }))
                          set('size', opt.value)
                        }}
                        disabled={!form.sizeCategoryId}>
                        <option value="">Select...</option>
                        {opts.sizes.map(s => <option key={s.id} value={s.value}>{s.label}</option>)}
                      </SelectWithAdd>}
                </div>
                <div>
                  <CardLabel>Colors</CardLabel>
                  {!isReadOnly && <FieldHint>Double-click to set primary.</FieldHint>}
                  <BubbleSelect
                    options={opts.colors.map(o => ({ value: o.id, label: o.label, hex: o.hex }))}
                    selected={form.colorIds} onChange={v => set('colorIds', v)}
                    colorMode readOnly={isReadOnly}
                    quickAdd={!isReadOnly ? { attribute: 'color', isColor: true, onAdded: opt => {
                      setOpts(prev => ({ ...prev, colors: [...prev.colors, opt] }))
                    }} : undefined} />
                </div>
              </div>
            </Card>

            <Card className="col-span-3 flex flex-col">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <span className="text-xs font-medium uppercase tracking-widest text-zinc-400">Attachments</span>
                  {!isReadOnly && <p className="text-xs text-zinc-500 italic mt-1 leading-relaxed">First photo is primary. Up to 20 photos.</p>}
                </div>
                {!isReadOnly && form.photos.filter(p => !p.isDeleted && p.file).length > 0 && (
                  <button
                    type="button"
                    onClick={handleIdentify}
                    disabled={identifying}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-brand-orange/10 text-brand-orange border border-brand-orange/30 hover:bg-brand-orange/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0">
                    <IconSparkles size={13} />
                    {identifying ? 'Identifying…' : 'Identify with AI'}
                  </button>
                )}
              </div>
              <div className="flex-1">
                <PhotoUpload
                  photos={form.photos}
                  onChange={v => set('photos', v)}
                  readOnly={isReadOnly} />
              </div>
            </Card>
          </div>

          {/* ── Row 3: Category & Condition Details — parent card with 2×2 sub-cards ── */}
          <Card>
            <CardHeader title="Category & Condition Details" pill={visibilityValue ? <VisibilityPill value={visibilityValue} /> : undefined} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <SubCard className="flex flex-col">
                <CardLabel>Role</CardLabel>
                <BubbleSelect
                  options={opts.roles.map(o => ({ value: o.id, label: o.label }))}
                  selected={form.roleIds} onChange={v => set('roleIds', v)}
                  readOnly={isReadOnly}
                  quickAdd={!isReadOnly ? { attribute: 'role', onAdded: opt => {
                    setOpts(prev => ({ ...prev, roles: [...prev.roles, opt] }))
                  }} : undefined} />
              </SubCard>
              <SubCard className="flex flex-col">
                <CardLabel>Scene</CardLabel>
                <BubbleSelect
                  options={opts.scenes.map(o => ({ value: o.id, label: o.label }))}
                  selected={form.sceneIds} onChange={v => set('sceneIds', v)}
                  readOnly={isReadOnly}
                  quickAdd={!isReadOnly ? { attribute: 'scene', onAdded: opt => {
                    setOpts(prev => ({ ...prev, scenes: [...prev.scenes, opt] }))
                  }} : undefined} />
              </SubCard>
              <SubCard className="flex flex-col">
                <CardLabel>Activity</CardLabel>
                <BubbleSelect
                  options={opts.activities.map(o => ({ value: o.id, label: o.label }))}
                  selected={form.activityIds} onChange={v => set('activityIds', v)}
                  readOnly={isReadOnly}
                  quickAdd={!isReadOnly ? { attribute: 'activity', onAdded: opt => {
                    setOpts(prev => ({ ...prev, activities: [...prev.activities, opt] }))
                  }} : undefined} />
              </SubCard>
              <SubCard className="flex flex-col">
                <CardLabel>Customizations</CardLabel>
                <BubbleSelect
                  options={opts.additives.map(o => ({ value: o.id, label: o.label }))}
                  selected={form.additiveIds} onChange={v => set('additiveIds', v)}
                  readOnly={isReadOnly}
                  quickAdd={!isReadOnly ? { attribute: 'additive', onAdded: opt => {
                    setOpts(prev => ({ ...prev, additives: [...prev.additives, opt] }))
                  }} : undefined} />
              </SubCard>
            </div>
          </Card>

          {/* ── Row 4: Buy/Sell/Trade (40%) + Notes (60%) ── */}
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
            <Card className="col-span-2 flex flex-col">
              <CardHeader title="Buy / Sell / Trade Details" pill={<VisibilityPill value="private" />} />
              <div className="space-y-3 flex-1">
                <div>
                  <CardLabel>Exchange Availability</CardLabel>
                  <RadioBubbles options={opts.exchange} value={form.exchangeAvailabilityId}
                    onChange={v => set('exchangeAvailabilityId', v)} readOnly={isReadOnly} />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Acquired From</p>
                  {isReadOnly
                    ? <ViewLabel>{form.acquisitionSource}</ViewLabel>
                    : <TextInput value={form.acquisitionSource} onChange={v => set('acquisitionSource', v)}
                        placeholder="e.g. eBay, local shop, gift..." />}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Date Acquired</p>
                    {isReadOnly
                      ? <ViewLabel>{form.acquisitionDate}</ViewLabel>
                      : <input type="date" value={form.acquisitionDate}
                          onChange={e => set('acquisitionDate', e.target.value)}
                          className="w-full px-3 py-2 bg-surface-raised border border-border-default rounded-lg text-sm text-zinc-200 outline-none focus:border-brand-orange/60 transition-colors" />}
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Product ID / SKU</p>
                    {isReadOnly
                      ? <ViewLabel>{form.productId}</ViewLabel>
                      : <TextInput value={form.productId} onChange={v => set('productId', v)}
                          placeholder="UPC, SKU..." />}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Acquisition Price</p>
                    {isReadOnly
                      ? <ViewLabel>{form.acquisitionPrice}</ViewLabel>
                      : <TextInput value={form.acquisitionPrice} onChange={v => set('acquisitionPrice', v)}
                          placeholder="0.00" />}
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Est. Value</p>
                    {isReadOnly
                      ? <ViewLabel>{form.tradeSellValue}</ViewLabel>
                      : <TextInput value={form.tradeSellValue} onChange={v => set('tradeSellValue', v)}
                          placeholder="0.00" />}
                  </div>
                </div>
              </div>
            </Card>

            <Card className="col-span-3 flex flex-col">
              <CardHeader title="Notes" pill={<VisibilityPill value="private" />} />
              {isReadOnly
                ? <ViewLabel>{form.notes}</ViewLabel>
                : <TextArea value={form.notes} onChange={v => set('notes', v)}
                    placeholder="Private notes — never shared..."
                    className="flex-1 resize-none min-h-0" />}
            </Card>
          </div>

          {/* ── Row 5: Sharing placeholder ── */}
          <Card>
            <span className="text-xs font-medium uppercase tracking-widest text-zinc-600">
              Sharing — coming soon
            </span>
          </Card>

        </div>
      </FullScreenModal>

      <UnsavedChangesDialog
        open={showUnsaved}
        onLeave={handleDiscardAndClose}
        onStay={() => setShowUnsaved(false)} />

      {aiSuggestions && (
        <AiSuggestionsPanel
          suggestions={aiSuggestions}
          opts={{
            conditions: opts.conditions,
            brands: opts.brands,
            activities: opts.activities,
            colors: opts.colors,
            roles: opts.roles,
            scenes: opts.scenes,
          }}
          onApplyField={handleAiApplyField}
          onClose={() => setAiSuggestions(null)} />
      )}
    </>
  )
}
